// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * AutoSave Context
 *
 * HIPAA Compliance: 164.312(a)(1) - Access Control
 *
 * This context provides draft autosave functionality using sessionStorage instead
 * of localStorage. This is critical for HIPAA compliance because:
 *
 * 1. sessionStorage is cleared when the browser tab/window is closed
 * 2. Draft data may contain PHI (patient names, conditions, notes)
 * 3. localStorage persists indefinitely, creating risk of unauthorized access
 *
 * By using sessionStorage:
 * - Drafts are automatically cleared when the session ends
 * - No PHI remains on the device after the browser is closed
 * - Combined with session timeout, this provides defense in depth
 */

import type { Resource } from '@medplum/fhirtypes';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { JSX, ReactNode } from 'react';

const STORAGE_KEY_PREFIX = 'carepoint-draft-';
const AUTOSAVE_DELAY_MS = 2000; // 2 seconds debounce

export interface DraftEntry {
  id: string;
  resourceType: string;
  data: Partial<Resource> | Record<string, unknown>;
  savedAt: number;
  organizationId?: string;
  description?: string;
}

interface AutoSaveContextValue {
  /** Whether there are any unsaved drafts */
  hasUnsavedChanges: boolean;
  /** List of all current drafts */
  drafts: DraftEntry[];
  /** Save a draft (debounced) */
  saveDraft: (
    id: string,
    resourceType: string,
    data: Partial<Resource> | Record<string, unknown>,
    description?: string
  ) => void;
  /** Save a draft immediately (no debounce) */
  saveDraftImmediate: (
    id: string,
    resourceType: string,
    data: Partial<Resource> | Record<string, unknown>,
    description?: string
  ) => void;
  /** Get a specific draft by ID */
  getDraft: (id: string) => DraftEntry | undefined;
  /** Remove a draft (after successful save) */
  removeDraft: (id: string) => void;
  /** Clear all drafts (on practice switch confirmation) */
  clearAllDrafts: () => void;
  /** Get drafts for the current organization */
  getCurrentOrgDrafts: () => DraftEntry[];
  /** Register that a form has unsaved changes */
  registerUnsavedForm: (formId: string) => void;
  /** Unregister a form (when saved or unmounted) */
  unregisterUnsavedForm: (formId: string) => void;
  /** Check if a specific form has unsaved changes */
  hasFormUnsavedChanges: (formId: string) => boolean;
}

const AutoSaveContext = createContext<AutoSaveContextValue | undefined>(undefined);

interface AutoSaveProviderProps {
  children: ReactNode;
  organizationId?: string;
}

export function AutoSaveProvider({ children, organizationId }: AutoSaveProviderProps): JSX.Element {
  const [drafts, setDrafts] = useState<DraftEntry[]>([]);
  const [unsavedForms, setUnsavedForms] = useState<Set<string>>(new Set());
  const debounceTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Load drafts from sessionStorage on mount
  // HIPAA: Using sessionStorage ensures drafts are cleared when browser closes
  useEffect(() => {
    const loadDrafts = (): void => {
      const loadedDrafts: DraftEntry[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(STORAGE_KEY_PREFIX)) {
          try {
            const draft = JSON.parse(sessionStorage.getItem(key) || '');
            if (draft && draft.id) {
              loadedDrafts.push(draft);
            }
          } catch {
            // Invalid JSON, skip
          }
        }
      }
      setDrafts(loadedDrafts);
    };
    loadDrafts();
  }, []);

  // Save draft to sessionStorage
  // HIPAA: Using sessionStorage ensures drafts are cleared when browser closes
  const persistDraft = useCallback(
    (
      id: string,
      resourceType: string,
      data: Partial<Resource> | Record<string, unknown>,
      description?: string
    ): void => {
      const draft: DraftEntry = {
        id,
        resourceType,
        data,
        savedAt: Date.now(),
        organizationId,
        description,
      };

      sessionStorage.setItem(`${STORAGE_KEY_PREFIX}${id}`, JSON.stringify(draft));
      setDrafts((prev) => {
        const filtered = prev.filter((d) => d.id !== id);
        return [...filtered, draft];
      });
    },
    [organizationId]
  );

  // Debounced save
  const saveDraft = useCallback(
    (
      id: string,
      resourceType: string,
      data: Partial<Resource> | Record<string, unknown>,
      description?: string
    ): void => {
      // Clear existing timer for this ID
      const existingTimer = debounceTimers.current.get(id);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // Set new timer
      const timer = setTimeout(() => {
        persistDraft(id, resourceType, data, description);
        debounceTimers.current.delete(id);
      }, AUTOSAVE_DELAY_MS);

      debounceTimers.current.set(id, timer);
    },
    [persistDraft]
  );

  // Immediate save (for critical moments)
  const saveDraftImmediate = useCallback(
    (
      id: string,
      resourceType: string,
      data: Partial<Resource> | Record<string, unknown>,
      description?: string
    ): void => {
      // Clear any pending debounced save
      const existingTimer = debounceTimers.current.get(id);
      if (existingTimer) {
        clearTimeout(existingTimer);
        debounceTimers.current.delete(id);
      }
      persistDraft(id, resourceType, data, description);
    },
    [persistDraft]
  );

  // Get a specific draft
  const getDraft = useCallback(
    (id: string): DraftEntry | undefined => {
      return drafts.find((d) => d.id === id);
    },
    [drafts]
  );

  // Remove a draft
  const removeDraft = useCallback((id: string): void => {
    sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${id}`);
    setDrafts((prev) => prev.filter((d) => d.id !== id));
  }, []);

  // Clear all drafts
  const clearAllDrafts = useCallback((): void => {
    drafts.forEach((draft) => {
      sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${draft.id}`);
    });
    setDrafts([]);
    setUnsavedForms(new Set());
  }, [drafts]);

  // Get drafts for current organization
  const getCurrentOrgDrafts = useCallback((): DraftEntry[] => {
    if (!organizationId) return drafts;
    return drafts.filter((d) => d.organizationId === organizationId);
  }, [drafts, organizationId]);

  // Register unsaved form
  const registerUnsavedForm = useCallback((formId: string): void => {
    setUnsavedForms((prev) => new Set(prev).add(formId));
  }, []);

  // Unregister unsaved form
  const unregisterUnsavedForm = useCallback((formId: string): void => {
    setUnsavedForms((prev) => {
      const next = new Set(prev);
      next.delete(formId);
      return next;
    });
  }, []);

  // Check if form has unsaved changes
  const hasFormUnsavedChanges = useCallback(
    (formId: string): boolean => {
      return unsavedForms.has(formId);
    },
    [unsavedForms]
  );

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      debounceTimers.current.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const hasUnsavedChanges = useMemo(
    () => unsavedForms.size > 0 || drafts.length > 0,
    [unsavedForms, drafts]
  );

  const value = useMemo(
    (): AutoSaveContextValue => ({
      hasUnsavedChanges,
      drafts,
      saveDraft,
      saveDraftImmediate,
      getDraft,
      removeDraft,
      clearAllDrafts,
      getCurrentOrgDrafts,
      registerUnsavedForm,
      unregisterUnsavedForm,
      hasFormUnsavedChanges,
    }),
    [
      hasUnsavedChanges,
      drafts,
      saveDraft,
      saveDraftImmediate,
      getDraft,
      removeDraft,
      clearAllDrafts,
      getCurrentOrgDrafts,
      registerUnsavedForm,
      unregisterUnsavedForm,
      hasFormUnsavedChanges,
    ]
  );

  return <AutoSaveContext.Provider value={value}>{children}</AutoSaveContext.Provider>;
}

export function useAutoSave(): AutoSaveContextValue {
  const context = useContext(AutoSaveContext);
  if (!context) {
    throw new Error('useAutoSave must be used within an AutoSaveProvider');
  }
  return context;
}

/**
 * Hook for autosaving form data
 * @param formId - Unique identifier for the form
 * @param resourceType - FHIR resource type
 * @param description - Human-readable description of the draft
 */
export function useFormAutoSave(
  formId: string,
  resourceType: string,
  description?: string
): {
  saveDraft: (data: Partial<Resource> | Record<string, unknown>) => void;
  saveDraftImmediate: (data: Partial<Resource> | Record<string, unknown>) => void;
  getDraft: () => DraftEntry | undefined;
  removeDraft: () => void;
  markAsUnsaved: () => void;
  markAsSaved: () => void;
} {
  const {
    saveDraft,
    saveDraftImmediate,
    getDraft,
    removeDraft,
    registerUnsavedForm,
    unregisterUnsavedForm,
  } = useAutoSave();

  const save = useCallback(
    (data: Partial<Resource> | Record<string, unknown>): void => {
      saveDraft(formId, resourceType, data, description);
    },
    [saveDraft, formId, resourceType, description]
  );

  const saveImmediate = useCallback(
    (data: Partial<Resource> | Record<string, unknown>): void => {
      saveDraftImmediate(formId, resourceType, data, description);
    },
    [saveDraftImmediate, formId, resourceType, description]
  );

  const get = useCallback((): DraftEntry | undefined => {
    return getDraft(formId);
  }, [getDraft, formId]);

  const remove = useCallback((): void => {
    removeDraft(formId);
    unregisterUnsavedForm(formId);
  }, [removeDraft, unregisterUnsavedForm, formId]);

  const markAsUnsaved = useCallback((): void => {
    registerUnsavedForm(formId);
  }, [registerUnsavedForm, formId]);

  const markAsSaved = useCallback((): void => {
    unregisterUnsavedForm(formId);
  }, [unregisterUnsavedForm, formId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      unregisterUnsavedForm(formId);
    };
  }, [formId, unregisterUnsavedForm]);

  return {
    saveDraft: save,
    saveDraftImmediate: saveImmediate,
    getDraft: get,
    removeDraft: remove,
    markAsUnsaved,
    markAsSaved,
  };
}
